import { getPayGateToFiatCheckoutMode, isPayGateToEnabledForChannel } from "@/config/paygateto";
import { getVisiblePaytreeMethods } from "@/config/paytree";
import { VORKASSE_ENABLED } from "@/config/vorkasse";
import { isRevolutAvailableForCountry, PROVIDERS } from "@/lib/paygateto/providers";

export type PaymentBrandId =
	"visa" | "mastercard" | "amex" | "applePay" | "googlePay" | "link" | "stripe" | "sepa" | "revolut";

/** Additional informational brands are opt-in so shared storefront surfaces stay unchanged. */
export type PaymentBrandDisplay = "default" | "productDetail" | "retatrutideUsLanding";

const DEFAULT_BRAND_ORDER: readonly PaymentBrandId[] = [
	"visa",
	"mastercard",
	"applePay",
	"googlePay",
	"sepa",
	"revolut",
];

const RETATRUTIDE_US_BRAND_ORDER: readonly PaymentBrandId[] = [
	"visa",
	"mastercard",
	"amex",
	"applePay",
	"googlePay",
	"link",
	"stripe",
];

const PRODUCT_DETAIL_BRAND_ORDER: readonly PaymentBrandId[] = [
	"visa",
	"mastercard",
	"amex",
	"applePay",
	"googlePay",
	"sepa",
	"revolut",
];

/** Informational availability, not a promise that a wallet works on this device.
 * Wallets confirmed for the former Stripe/card route do not imply Airwallex support.
 * Interactive selection must supply its already filtered eligibility list.
 *
 * Brands follow what the checkout actually renders, not every enabled provider:
 * PaymentMethodSelector shows the PayGate catalog tiles (card, Apple Pay, Google
 * Pay, ...) only in `legacy_direct` mode. In `multi_provider` mode the standalone
 * Revolut Pay tile is the only PayGate method left, so card and wallet logos would
 * advertise methods the buyer cannot choose.
 */
export function getPaymentBrands(context: {
	channel: string;
	countryCode?: string;
	surface: "information" | "selection";
	availableBrands?: readonly PaymentBrandId[];
	display?: PaymentBrandDisplay;
}): readonly PaymentBrandId[] {
	const channel = context.channel.toLowerCase();
	if (channel !== "eu" && channel !== "us") return [];
	if (context.surface === "selection" && !context.availableBrands) return [];
	const catalogTilesOffered = getPayGateToFiatCheckoutMode() === "legacy_direct";
	const providers = isPayGateToEnabledForChannel(channel)
		? PROVIDERS.filter((provider) => provider.enabled && provider.channels.includes(channel))
		: [];
	const methods = new Set(
		providers
			.flatMap((provider) => provider.paymentMethods)
			.filter((method) => catalogTilesOffered || method === "revolut-pay"),
	);
	const stripeCard = getVisiblePaytreeMethods(channel).some((method) => method.apiMethod === "card");
	const showRetatrutideUsBrands =
		context.surface === "information" && context.display === "retatrutideUsLanding" && channel === "us";
	const showProductDetailBrands = context.surface === "information" && context.display === "productDetail";
	const allowed: Record<PaymentBrandId, boolean> = {
		visa: stripeCard || methods.has("card"),
		mastercard: stripeCard || methods.has("card"),
		amex: (showProductDetailBrands || showRetatrutideUsBrands) && stripeCard,
		applePay: stripeCard || methods.has("apple-pay"),
		googlePay: stripeCard || methods.has("google-pay"),
		link: showRetatrutideUsBrands && stripeCard,
		stripe: showRetatrutideUsBrands && stripeCard,
		sepa: channel === "eu" && (VORKASSE_ENABLED || methods.has("sepa")),
		revolut: methods.has("revolut-pay") && isRevolutAvailableForCountry(context.countryCode),
	};
	const brandOrder = showRetatrutideUsBrands
		? RETATRUTIDE_US_BRAND_ORDER
		: showProductDetailBrands
			? PRODUCT_DETAIL_BRAND_ORDER
			: DEFAULT_BRAND_ORDER;
	return brandOrder.filter(
		(id) => allowed[id] && (!context.availableBrands || context.availableBrands.includes(id)),
	);
}
